// src/components/games/OldschoolDungeonEscape.tsx
import { logger } from '../../utils/logger';

import React, { useState, useEffect, useRef } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonIcon,
  IonButtons,
  IonAlert,
} from '@ionic/react';
import { arrowBack, save, refresh, help } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import './OldschoolDungeonEscape.css';

// Import dungeon modules
import { Character, GameState, Room } from './dungeon/dungeonTypes';
import { initializeDungeon } from './dungeon/dungeonData';
import {
  createInitialGameState,
  createInitialCharacter,
  createInitialRunes,
  updateRoomDescriptions,
  calculateProgress,
  saveGameToStorage,
  loadGameFromStorage,
  hasSavedGame,
} from './dungeon/dungeonState';
import { deleteCloudSave } from '../../services/oldschoolDungeonEscapeService';
import { generateSolvablePuzzle, formatPuzzleDisplay } from './dungeon/dungeonPuzzles';
import { getHint } from './dungeon/dungeonHints';
import { displayMap } from './dungeon/dungeonMap';
import {
  handleMove,
  handleTake,
  handleDrop,
  handleExamine,
  handleMoveItem,
  handleUseItem,
  CommandHandlers,
} from './dungeon/dungeonCommands';

const OldschoolDungeonEscape: React.FC = () => {
  const history = useHistory();
  const [isInitialized, setIsInitialized] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [output, setOutput] = useState<string[]>([]);
  const [command, setCommand] = useState('');
  const [dungeon, setDungeon] = useState<{ [key: string]: Room }>({});
  const [char, setChar] = useState<Character>(createInitialCharacter());
  const [gs, setGs] = useState<GameState>(createInitialGameState());
  const [runes, setRunes] = useState<string[]>(createInitialRunes());
  const [showSaveAlert, setShowSaveAlert] = useState(false);
  const [showLoadAlert, setShowLoadAlert] = useState(false);
  const [showResetAlert, setShowResetAlert] = useState(false);
  const [hasSave, setHasSave] = useState(false);
  const outputRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const initRef = useRef(false);

  useEffect(() => {
    // Prevent double initialization in React strict mode
    if (initRef.current) return;
    initRef.current = true;

    // Initialize dungeon on mount
    const initialDungeon = initializeDungeon();
    setDungeon(initialDungeon);
    setIsInitialized(true);

    // Check for saved game after initialization (async)
    hasSavedGame().then((hasSavedGame) => {
      setHasSave(hasSavedGame);
      if (hasSavedGame) {
        setShowLoadAlert(true);
      }
    });
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when output changes
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  const print = (text: string) => {
    setOutput((prev) => [...prev, text]);
  };

  const describe = (room: Room, full: boolean = false) => {
    if (!room.v) {
      room.v = true;
    }

    print('You are in ' + room.ld);

    if (room.c && room.c.length > 0) {
      print('You can see: ' + room.c.join(', '));
    }

    if (full) {
      const exits = Object.keys(room.e);
      if (exits.length > 0) {
        print('Obvious exits: ' + exits.join(', '));
      }
    }
  };

  const showPuzzle = () => {
    const puzzleLines = formatPuzzleDisplay(gs.grid, runes);
    puzzleLines.forEach((line) => print(line));
    print('Available runes to move: ' + runes.slice(1).join(', '));
    print('To move a rune, type: move [rune name]');
  };

  const moveToRoom = (roomName: string) => {
    const newRoom = dungeon[roomName];
    if (!newRoom) {
      print('Error: Room not found!');
      return;
    }

    char.loc = roomName;
    setChar({ ...char });
    describe(newRoom);

    // Show puzzle if entering puzzle room
    if (roomName === 'puzzle room' && !gs.puzz) {
      print('');
      showPuzzle();
    }
  };

  const handleGameComplete = () => {
    // This is called from handleUseItem which already prints the completion message
    // So we don't need to print anything here - just show final stats
    print('');
    const progress = calculateProgress(dungeon, gs);
    print(`Final Stats:`);
    print(`- Rooms Explored: ${progress.vis}/${progress.totRooms}`);
    print(`- Puzzles Solved: ${progress.comp}/${progress.tot}`);
    print(`- Completion: ${progress.pct}%`);
    print('');
    print('Thank you for playing! Type "restart" to play again.');
  };

  const processCommand = (cmd: string) => {
    cmd = cmd.trim().toLowerCase();
    if (!cmd) return;

    print('> ' + cmd);

    const room = dungeon[char.loc];
    if (!room) {
      print('Error: Invalid room state!');
      return;
    }

    const handlers: CommandHandlers = {
      print,
      showPuzzle,
      describe,
      moveToRoom,
      onGameComplete: handleGameComplete,
    };

    const words = cmd.split(/\s+/);
    const verb = words[0];
    const obj = words.slice(1).join(' ');

    // Handle commands
    if (['north', 'south', 'east', 'west', 'up', 'down', 'n', 's', 'e', 'w', 'u', 'd'].includes(verb)) {
      handleMove(verb, char, gs, dungeon, room, handlers);
      setChar({ ...char });
      setGs({ ...gs });
      setDungeon({ ...dungeon });
      updateRoomDescriptions(dungeon, gs);
    } else if (['take', 'get', 'pickup'].includes(verb)) {
      handleTake(obj, char, room, handlers);
      setChar({ ...char });
      setDungeon({ ...dungeon });
      updateRoomDescriptions(dungeon, gs);
    } else if (['drop', 'put'].includes(verb)) {
      handleDrop(obj, char, room, handlers);
      setChar({ ...char });
      setDungeon({ ...dungeon });
      updateRoomDescriptions(dungeon, gs);
    } else if (['examine', 'look', 'x', 'l'].includes(verb)) {
      handleExamine(obj, char, gs, dungeon, room, handlers);
    } else if (['inventory', 'inv', 'i'].includes(verb)) {
      if (char.inv.length === 0) {
        print('You are not carrying anything.');
      } else {
        print('You are carrying: ' + char.inv.join(', '));
      }
    } else if (verb === 'move' && obj) {
      handleMoveItem(obj, char, gs, dungeon, room, runes, handlers);
      setChar({ ...char });
      setGs({ ...gs });
      setDungeon({ ...dungeon });
      setRunes([...runes]);
      updateRoomDescriptions(dungeon, gs);
    } else if (verb === 'use' && obj) {
      handleUseItem(obj, char, gs, dungeon, room, runes, handlers);
      setChar({ ...char });
      setGs({ ...gs });
      setDungeon({ ...dungeon });
      setRunes([...runes]);
      updateRoomDescriptions(dungeon, gs);
    } else if (verb === 'map') {
      if (char.inv.includes('dungeon map')) {
        displayMap(char, dungeon, gs, print);
      } else {
        print('You need a dungeon map to use this command. Try creating one with paper and a quill pen.');
      }
    } else if (verb === 'hint') {
      print(getHint(char, gs, dungeon));
    } else if (verb === 'help') {
      showHelp();
    } else if (verb === 'save') {
      handleSave();
    } else if (verb === 'load') {
      handleLoad();
    } else if (verb === 'restart') {
      setShowResetAlert(true);
    } else if (verb === 'puzzle' && char.loc === 'puzzle room') {
      showPuzzle();
    } else {
      print("I don't understand that command. Type 'help' for a list of commands.");
    }

    setCommand('');

    // Keep focus on input after command
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const showHelp = () => {
    print('=== COMMANDS ===');
    print('Movement: north/n, south/s, east/e, west/w, up/u, down/d');
    print('Actions: take [item], drop [item], use [item], move [item]');
    print('Looking: look/l, examine/x [thing], inventory/inv/i');
    print('Game: save, load, restart, hint, help');
    print('Special: map (requires dungeon map item), puzzle (in puzzle room)');
    print('');
    print('Tip: You can examine rooms, items, and objects you see mentioned in descriptions!');
  };

  const handleSave = async () => {
    const success = await saveGameToStorage(char, gs, dungeon, runes);
    if (success) {
      print('Game saved successfully! (Cloud + Local)');
      setShowSaveAlert(true);
      setHasSave(true);
    } else {
      print('Error saving game. Please try again.');
    }
  };

  const handleLoad = async () => {
    const result = await loadGameFromStorage(char, gs, dungeon, runes);
    if (result.success) {
      setChar({ ...char });
      setGs({ ...gs });
      setDungeon({ ...dungeon });
      setRunes([...runes]);
      print(`Game loaded successfully! (from ${result.source})`);
      print('');
      describe(dungeon[char.loc], true);
    } else {
      print('No saved game found or error loading game.');
    }
  };

  const handleReset = async () => {
    const newDungeon = initializeDungeon();
    const newChar = createInitialCharacter();
    const newGs = createInitialGameState();
    const newRunes = createInitialRunes();
    newGs.grid = generateSolvablePuzzle();

    setDungeon(newDungeon);
    setChar(newChar);
    setGs(newGs);
    setRunes(newRunes);
    setOutput([]);
    setGameStarted(false);

    // Clear saved game (both local and cloud)
    localStorage.removeItem('dungeonEscapeSave');
    await deleteCloudSave();
    setHasSave(false);
  };

  const startGame = async (loadSaved: boolean = false) => {
    if (!isInitialized) {
      logger.error('Cannot start game - not initialized yet');
      return;
    }

    setGameStarted(true);
    setOutput([]);

    if (loadSaved) {
      const result = await loadGameFromStorage(char, gs, dungeon, runes);
      if (result.success) {
        setChar({ ...char });
        setGs({ ...gs });
        setDungeon({ ...dungeon });
        setRunes([...runes]);
        print('🗝️ DUNGEON ESCAPE 🗝️');
        print('');
        print(`Game loaded from ${result.source}! Welcome back, adventurer.`);
        print('');
        describe(dungeon[char.loc], true);
      } else {
        startNewGame();
      }
    } else {
      startNewGame();
    }
  };

  const startNewGame = () => {
    // Initialize fresh game
    const newDungeon = initializeDungeon();
    const newChar = createInitialCharacter();
    const newGs = createInitialGameState();
    const newRunes = createInitialRunes();
    newGs.grid = generateSolvablePuzzle();

    setDungeon(newDungeon);
    setChar(newChar);
    setGs(newGs);
    setRunes(newRunes);

    print('🗝️ DUNGEON ESCAPE 🗝️');
    print('');
    print('You awaken in a dark dungeon with no memory of how you got here.');
    print('Your only goal: find a way to escape!');
    print('');
    print("Type 'help' for commands or 'hint' if you get stuck.");
    print('');
    describe(newDungeon[newChar.loc], true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (command.trim()) {
      processCommand(command);
      setCommand(''); // Clear input after sending
      // Keep focus on input to maintain keyboard
      setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
    }
  };

  // Show loading state until initialized
  if (!isInitialized) {
    return (
      <IonPage className="dungeon-escape-page">
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => history.goBack()}>
                <IonIcon icon={arrowBack} />
              </IonButton>
            </IonButtons>
            <IonTitle>Dungeon Escape</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="dungeon-content">
          <div className="start-screen">
            <h1 className="game-text">🗝️ DUNGEON ESCAPE 🗝️</h1>
            <p className="game-text">Initializing...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!gameStarted) {
    return (
      <IonPage className="dungeon-escape-page">
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => history.goBack()}>
                <IonIcon icon={arrowBack} />
              </IonButton>
            </IonButtons>
            <IonTitle>Dungeon Escape</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="dungeon-content dungeon-escape-container">
          <div className="start-screen">
            <h1 className="game-text">🗝️ DUNGEON ESCAPE 🗝️</h1>
            <p className="game-text">
              A classic text-based adventure game. Explore mysterious dungeons, solve intricate
              puzzles, and uncover ancient secrets to find your way out!
            </p>
            <p className="game-text">
              Features a sliding block puzzle, map system, and progressive hints to help you on
              your journey.
            </p>
            {hasSave && (
              <IonButton expand="block" onClick={() => startGame(true)} style={{ marginBottom: '1rem', maxWidth: '300px' }}>
                Continue Saved Game
              </IonButton>
            )}
            <IonButton expand="block" onClick={() => startGame(false)} style={{ maxWidth: '300px' }}>
              {hasSave ? 'Start New Game' : 'Start Adventure'}
            </IonButton>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage className="dungeon-escape-page">
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={() => history.goBack()}>
              <IonIcon icon={arrowBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>Dungeon Escape</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => processCommand('hint')}>
              <IonIcon icon={help} />
            </IonButton>
            <IonButton onClick={() => processCommand('save')}>
              <IonIcon icon={save} />
            </IonButton>
            <IonButton onClick={() => setShowResetAlert(true)}>
              <IonIcon icon={refresh} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="dungeon-content dungeon-escape-container" scrollY={false}>
        <div className="game-container">
          <div className="output game-output" ref={outputRef}>
            {output.map((line, index) => (
              <p key={index} className="game-text">{line}</p>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="input-area">
            <input
              ref={inputRef}
              type="text"
              className="command-input"
              value={command}
              onChange={(e) => setCommand(e.target.value)}
              placeholder="Enter command..."
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            <IonButton type="submit">Send</IonButton>
          </form>
        </div>
      </IonContent>

      <IonAlert
        isOpen={showSaveAlert}
        onDidDismiss={() => setShowSaveAlert(false)}
        header="Game Saved"
        message="Your progress has been saved successfully!"
        buttons={['OK']}
      />

      <IonAlert
        isOpen={showLoadAlert}
        onDidDismiss={() => setShowLoadAlert(false)}
        header="Continue Game?"
        message="A saved game was found. Would you like to continue where you left off?"
        buttons={[
          {
            text: 'New Game',
            handler: () => startGame(false),
          },
          {
            text: 'Continue',
            handler: () => startGame(true),
          },
        ]}
      />

      <IonAlert
        isOpen={showResetAlert}
        onDidDismiss={() => setShowResetAlert(false)}
        header="Restart Game?"
        message="Are you sure you want to restart? Your current progress will be lost."
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Restart',
            handler: () => handleReset(),
          },
        ]}
      />
    </IonPage>
  );
};

export default OldschoolDungeonEscape;